"use client";

import { useState } from "react";
import { supabase } from "@/lib/supabase/client";
import { useTaskStore } from "@/stores/taskStore";
import type { InteractionRequest } from "@bash/shared";
import { useConvergingRead } from "./useConvergingRead";

export interface PendingInteractionRow {
  id: string;
  task_id: string;
  user_id: string;
  kind: InteractionRequest["kind"];
  renderer: string | null;
  request: InteractionRequest;
  state: "pending" | "resolved" | "cancelled" | "expired";
  created_at: string;
  expires_at: string;
  /**
   * Joined `tasks.title` + `tasks.origin_channel` + `tasks.routine_id`. Present
   * on rows fetched via the initial select (`*, tasks(title, origin_channel,
   * routine_id)`). NOTE:
   * Supabase Realtime INSERT/UPDATE payloads do NOT include the join — Realtime
   * emits the raw row only. Fresh INSERTs are enriched by a one-row joined
   * re-fetch so the card carries the real task title instead of a placeholder;
   * the channel rides that same enrichment, no extra query. `routine_id` is how a
   * routine's own views tell that one of its runs is waiting on the person.
   */
  tasks?: {
    title: string | null;
    origin_channel?: string | null;
    routine_id?: string | null;
  } | null;
}

/**
 * The level-triggered RETRACTION rail (07-28 stuck-composer incident).
 *
 * Retraction of a task's pendingInteraction used to be push-only: the realtime
 * UPDATE edge plus AGENT_START's auth-only sweep. OAuth backgrounds this tab BY
 * CONSTRUCTION (the card window.open's the authorize page), so the out-of-band
 * resolve fires while the tab is asleep, every push is missed, and the composer
 * stays locked on a card whose interaction row is long resolved.
 *
 * The converging read below already re-fetches the authoritative pending set on
 * every wake/heartbeat. This rail diffs the TASK STORE against that set: any
 * held pending (head, queue tail, subagent lane) whose row is absent gets a
 * one-row source-of-truth confirm, and only a row seen in a non-pending state
 * retracts. A MISSING row never retracts — a just-raised card whose insert
 * hasn't landed in the read must stay up (doubt keeps the card).
 */
export async function reconcileRetractedPendings(
  freshPendingIds: ReadonlySet<string>,
  deps: {
    tasks: ReadonlyMap<
      string,
      {
        pendingInteraction: {
          interactionId?: string;
          requestId: string;
        } | null;
        pendingQueue?: Array<{ interactionId?: string; requestId: string }>;
        pendingByLane?: Record<
          string,
          { interactionId?: string; requestId: string }
        >;
      }
    >;
    /** One-row truth probe: 'pending' | 'settled' (row left pending) | 'gone' (no row). */
    confirmState: (
      interactionId: string
    ) => Promise<"pending" | "settled" | "gone">;
    reconcile: (taskId: string, interactionId: string) => void;
  }
): Promise<void> {
  // Fire-and-forget caller: an absent/non-iterable map (mocked stores, early
  // boot) must be a no-op, never a rejection.
  if (typeof deps.tasks?.[Symbol.iterator] !== "function") return;
  for (const [taskId, task] of deps.tasks) {
    const held = [
      ...(task.pendingInteraction ? [task.pendingInteraction] : []),
      ...(task.pendingQueue ?? []),
      ...Object.values(task.pendingByLane ?? {}),
    ];
    for (const pi of held) {
      const key = pi.interactionId ?? pi.requestId;
      if (!key || freshPendingIds.has(key)) continue;
      try {
        if ((await deps.confirmState(key)) === "settled") {
          deps.reconcile(taskId, key);
        }
      } catch {
        // Doubt keeps the card — the next wake re-runs this rail anyway.
      }
    }
  }
}

/** The row plus the task columns every card and routine view reads. */
const PENDING_SELECT = "*, tasks(title, origin_channel, routine_id)";

/** Prepend a fresh realtime row, deduping by id. Exported for tests. */
export function upsertPendingRow(
  prev: PendingInteractionRow[],
  row: PendingInteractionRow
): PendingInteractionRow[] {
  return [row, ...prev.filter((p) => p.id !== row.id)];
}

/**
 * Merge a joined re-fetch into the list; never downgrade a known title.
 *
 * Insert-if-absent, because a realtime INSERT whose payload arrived without
 * its `request` is deliberately not rendered (see the INSERT handler): this
 * re-fetch is that row's only way onto the screen. Only a row the database
 * still calls `pending` is added — one that settled while the re-fetch was in
 * flight must not come back.
 */
export function mergeEnrichedRow(
  prev: PendingInteractionRow[],
  enriched: PendingInteractionRow
): PendingInteractionRow[] {
  if (!prev.some((p) => p.id === enriched.id)) {
    return enriched.state === "pending"
      ? upsertPendingRow(prev, enriched)
      : prev;
  }
  return prev.map((p) =>
    p.id === enriched.id ? { ...enriched, tasks: enriched.tasks ?? p.tasks } : p
  );
}

export function useInteractionsRealtime(userId: string | null): {
  pending: PendingInteractionRow[];
  loading: boolean;
} {
  const [pending, setPending] = useState<PendingInteractionRow[]>([]);

  // All wake/retry/probe machinery lives in the shared rail (useConvergingRead)
  // — this hook only states its query and how realtime rows fold into the list.
  const { loading } = useConvergingRead<PendingInteractionRow>({
    table: "interactions",
    userId,
    read: async (signal) => {
      const { data, error } = await supabase
        .from("interactions")
        .select(PENDING_SELECT)
        .eq("user_id", userId ?? "")
        .eq("state", "pending")
        .order("created_at", { ascending: false })
        .abortSignal(signal)
        .limit(50);
      if (error) throw error;
      return (data ?? []) as PendingInteractionRow[];
    },
    apply: (rows) => {
      setPending(rows);
      // Level-triggered retraction: settle any task-store pending the fresh
      // authoritative set no longer holds (fire-and-forget; never blocks apply).
      void reconcileRetractedPendings(new Set(rows.map((r) => r.id)), {
        tasks: useTaskStore.getState().tasks,
        confirmState: async (interactionId) => {
          const { data, error } = await supabase
            .from("interactions")
            .select("state")
            .eq("id", interactionId)
            .maybeSingle();
          if (error) throw error;
          if (!data) return "gone";
          return (data as { state: string }).state === "pending"
            ? "pending"
            : "settled";
        },
        reconcile: (taskId, interactionId) =>
          useTaskStore
            .getState()
            .reconcileServerInteraction(taskId, interactionId),
      }).catch(() => {
        // Never let the rail reject out of a void call — the next wake retries.
      });
    },
    changes: [
      {
        event: "INSERT",
        handler: (payload) => {
          // Realtime payload.new is the raw row — no joined tasks.title.
          const row = payload.new as PendingInteractionRow;
          if (row.state !== "pending") return;
          // Realtime leaves a TOASTed or oversized column out of the payload,
          // and `request` is a large jsonb. Every card is derived from it, so
          // a row without it is a full-page crash, not a thinner card: hold it
          // back and let the joined re-fetch below be what puts it on screen.
          if (row.request) setPending((prev) => upsertPendingRow(prev, row));
          // One-row joined re-fetch so the fresh card carries the real task
          // title instead of a placeholder — and, for a row held back above,
          // the thing that puts it on screen at all. Fire-and-forget either
          // way: a row that carried its request is already rendering.
          void supabase
            .from("interactions")
            .select(PENDING_SELECT)
            .eq("id", row.id)
            .single()
            .then(
              ({ data }) => {
                if (data)
                  setPending((prev) =>
                    mergeEnrichedRow(prev, data as PendingInteractionRow)
                  );
              },
              // A failure costs only the joined title for a row that carried
              // its request, since that one already renders. A row held back
              // for want of its request waits instead for the next converging
              // read (wake, channel rejoin, or the 30s heartbeat) to carry it.
              () => {}
            );
        },
      },
      {
        event: "UPDATE",
        handler: (payload) => {
          // Realtime payload.new is the raw row — no joined tasks.title, and
          // no unchanged TOASTed column either. For pending→pending updates,
          // preserve any title we already have from the initial joined fetch,
          // and the `request` we already hold: the payload omitting it means
          // "unchanged", never "gone", and every card is derived from it.
          const row = payload.new as PendingInteractionRow;
          setPending((prev) =>
            row.state === "pending"
              ? prev.map((p) =>
                  p.id === row.id
                    ? {
                        ...row,
                        request: row.request ?? p.request,
                        tasks: p.tasks ?? row.tasks,
                      }
                    : p
                )
              : prev.filter((p) => p.id !== row.id)
          );
          // Server truth: this interaction left `pending` (resolved / cancelled
          // / superseded / completed) by something other than this client's own
          // Submit. Retract the live picker on the matching task so the input
          // re-enables. Scoped + idempotent in the store action.
          if (row.state !== "pending") {
            useTaskStore
              .getState()
              .reconcileServerInteraction(row.task_id, row.id);
          }
        },
      },
    ],
  });

  return { pending, loading };
}
